import { useCallback, useMemo } from 'react';
import { getHours, getMinutes, getSeconds, isDate, isValid, set } from 'date-fns';

import styled from '@emotion/styled';
import { Box, Select } from '@kc-console/common-ui';

import { getTimezoneOffset } from '../../utils/timezone';
import { buildTimeOptions, snapToInterval } from './utils';

import type { TimeChangePayload, TimePickerProps } from './types';

const TimePicker = ({
  size = 'md',
  value,
  onChange,
  disabled = false,
  showSeconds = true,
  showTimezone = false,
  fullWidth = false,
  minuteInterval = 1,
  hourInterval = 1,
  secondInterval = 1,
  name,
}: TimePickerProps) => {
  const isValidDate = value != null && isDate(value) && isValid(value);

  const hourOptions = useMemo(() => buildTimeOptions(24, hourInterval), [hourInterval]);
  const minuteOptions = useMemo(() => buildTimeOptions(60, minuteInterval), [minuteInterval]);
  const secondOptions = useMemo(() => buildTimeOptions(60, secondInterval), [secondInterval]);

  const currentHour = isValidDate ? snapToInterval(getHours(value), hourInterval) : undefined;
  const currentMinute = isValidDate ? snapToInterval(getMinutes(value), minuteInterval) : undefined;
  const currentSecond = isValidDate ? getSeconds(value) : undefined;

  // 통합된 시간 변경 핸들러 (date-fns 사용으로 불변성 유지)
  const handleTimeChange = useCallback(
    (timePart: TimeChangePayload) => {
      // 유효한 날짜가 있으면 그것을 기준으로, 없으면 현재 시점을 기준으로 새 객체 생성
      const baseDate = isValidDate ? value : new Date();

      // set 함수를 사용하여 불변성을 유지하며 시간 변경
      const newDate = set(baseDate, {
        hours: timePart.hour ?? getHours(baseDate),
        minutes: timePart.minute ?? getMinutes(baseDate),
        seconds: timePart.second ?? getSeconds(baseDate),
      });

      onChange?.(newDate);
    },
    [isValidDate, value, onChange],
  );

  // 시간 변경 핸들러
  const handleHourChange = useCallback(
    (hour: number | null) => {
      if (hour === null) {
        onChange?.(null);
        return;
      }
      handleTimeChange({ hour });
    },
    [handleTimeChange, onChange],
  );

  // 분 변경 핸들러
  const handleMinuteChange = useCallback(
    (minute: number | null) => {
      if (minute === null) {
        onChange?.(null);
        return;
      }
      handleTimeChange({ minute });
    },
    [handleTimeChange, onChange],
  );

  // 초 변경 핸들러
  const handleSecondChange = useCallback(
    (second: number | null) => {
      if (second === null) {
        onChange?.(null);
        return;
      }
      handleTimeChange({ second });
    },
    [handleTimeChange, onChange],
  );

  const timePickerContent = (
    <Container fullWidth={fullWidth}>
      <TimeSelect
        key={'hour'}
        name={name ? `${name}.hour` : undefined}
        size={size}
        options={hourOptions}
        value={currentHour}
        onChange={handleHourChange}
        disabled={disabled}
        placeholder="HH"
        className="react-datepicker-ignore-onclickoutside"
        DropdownMenuProps={{
          className: 'react-datepicker-ignore-onclickoutside',
        }}
      />
      <TimeSeparator>:</TimeSeparator>
      <TimeSelect
        key={'minute'}
        name={name ? `${name}.minute` : undefined}
        size={size}
        options={minuteOptions}
        value={currentMinute}
        onChange={handleMinuteChange}
        disabled={disabled}
        placeholder="MM"
        className="react-datepicker-ignore-onclickoutside"
        DropdownMenuProps={{
          className: 'react-datepicker-ignore-onclickoutside',
        }}
      />
      {showSeconds && (
        <>
          <TimeSeparator>:</TimeSeparator>
          <TimeSelect
            key={'second'}
            name={name ? `${name}.second` : undefined}
            size={size}
            options={secondOptions}
            value={currentSecond}
            onChange={handleSecondChange}
            disabled={disabled}
            placeholder="SS"
            className="react-datepicker-ignore-onclickoutside"
            DropdownMenuProps={{
              className: 'react-datepicker-ignore-onclickoutside',
            }}
          />
        </>
      )}
    </Container>
  );

  return showTimezone ? (
    <TimezoneBox>
      {timePickerContent}
      <TimezoneText>{getTimezoneOffset()}</TimezoneText>
    </TimezoneBox>
  ) : (
    timePickerContent
  );
};

const TimeSelect = styled(Select<number>)`
  flex: 1;
`;

const Container = styled(Box, {
  shouldForwardProp: prop => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ fullWidth }) => fullWidth && 'flex: 1;'}
`;

const TimeSeparator = styled.span`
  ${({ theme }) => theme.typography['label/regular/md']};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const TimezoneBox = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimezoneText = styled.span`
  display: inline-flex;
  ${({ theme }) => theme.typography['body/normal/md']};
  white-space: nowrap;
`;

export default TimePicker;
